/**
 * CuentaLayout — shell del dashboard cliente.
 *
 * Responsive:
 *  - `≥ lg`: sidebar lateral sticky a la izquierda (240px).
 *  - `< lg`: topbar con botón "Menú" + nombre de la sección activa. La nav
 *    se abre como `<Drawer side="left">` que se cierra al navegar.
 *
 * F5a añadió el botón "Cerrar sesión" al pie del sidebar (ahora vive en
 * `<AccountSidebar variant="cuenta">`). El guard de auth vive en
 * `<RequireAuth>` (router.tsx).
 */
import { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  Calendar,
  Heart,
  LayoutDashboard,
  MessageSquare,
  Menu,
  Settings,
  ShoppingBag,
} from 'lucide-react';
import { Navbar } from '../../components/layout/Navbar';
import { Footer } from '../../components/layout/Footer';
import { Container } from '../../components/layout/Container';
import { SkipLink } from '../../components/layout/SkipLink';
import { ScrollToTop } from '../../components/layout/ScrollToTop';
import { ScrollToTopButton } from '../../components/layout/ScrollToTopButton';
import { AccountSidebar, type AccountSidebarItem } from '../../components/layout/AccountSidebar';
import { Drawer } from '../../components/ui/Drawer';
import { Routes } from '../../lib/routes';
import { cn } from '../../lib/cn';

const navItems: AccountSidebarItem[] = [
  { to: Routes.account, label: 'Inicio', icon: LayoutDashboard, end: true },
  { to: Routes.accountAppointments, label: 'Mis citas', icon: Calendar },
  { to: Routes.accountOrders, label: 'Mis pedidos', icon: ShoppingBag },
  { to: Routes.accountFavorites, label: 'Favoritos', icon: Heart },
  { to: Routes.accountMessages, label: 'Mensajes', icon: MessageSquare },
  { to: Routes.accountSettings, label: 'Configuración', icon: Settings },
];

/**
 * Resuelve el label de la sección activa a partir del pathname.
 * Hace match exacto primero (para `/cuenta`), luego prefijo más largo.
 */
function resolveActiveLabel(pathname: string): string {
  const exact = navItems.find((item) => item.to === pathname);
  if (exact) return exact.label;
  const prefix = navItems
    .filter((item) => pathname.startsWith(item.to + '/') || pathname.startsWith(item.to))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return prefix?.label ?? 'Mi cuenta';
}

export function CuentaLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();
  const activeLabel = useMemo(() => resolveActiveLabel(pathname), [pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-cream-100">
      {/* Restore scroll al cambiar de subruta dentro de /cuenta/*. */}
      <ScrollToTop />
      <SkipLink />
      <Navbar />
      <main id="main" tabIndex={-1} className="flex-1 py-8 lg:py-12 focus:outline-none">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
            {/* Desktop sidebar: visible >= lg. Card premium con borde dorado y
                ornamento de estrella en la esquina superior derecha (decorativo
                con baja opacidad). */}
            <aside aria-label="Navegación de cuenta" className="hidden lg:block">
              <div
                className={cn(
                  'relative isolate overflow-hidden rounded-lg lg:rounded-[20px]',
                  'border border-gold-500/35 bg-cream-50',
                  'shadow-elev1',
                  'lg:sticky lg:top-24',
                )}
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute right-3 top-3 select-none text-xs text-gold-500/70"
                >
                  ✦
                </span>
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -left-12 -bottom-12 h-32 w-32 rounded-pill bg-gold-500/10 blur-3xl"
                />
                <AccountSidebar items={navItems} variant="cuenta" />
              </div>
            </aside>

            <div className="min-w-0">
              {/* Mobile topbar: visible < lg (card crema con borde dorado). */}
              <div className="lg:hidden mb-6">
                <div className="flex items-center gap-3 p-3 bg-cream-50 rounded-lg shadow-elev1 border border-gold-500/35">
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    aria-label="Abrir navegación de cuenta"
                    aria-expanded={drawerOpen}
                    aria-controls="cuenta-drawer"
                    className={cn(
                      'inline-flex items-center justify-center',
                      'min-h-11 min-w-11 rounded text-primary-700',
                      'hover:bg-cream-200 transition-colors duration-2 ease-out',
                    )}
                  >
                    <Menu className="h-6 w-6" aria-hidden="true" />
                  </button>
                  <p className="font-display text-h3 text-primary-700 truncate">{activeLabel}</p>
                </div>
              </div>

              <Outlet />
            </div>
          </div>
        </Container>
      </main>

      {/* Drawer: montado fuera del grid; solo visible cuando drawerOpen y < lg.
          En >= lg jamás se abre (sin botón), pero también lo ocultamos por
          si el usuario redimensiona la ventana con el drawer abierto. */}
      <div id="cuenta-drawer" className="lg:hidden">
        <Drawer.Root
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          side="left"
          size="sm"
          title="Mi cuenta"
          hideTitle
        >
          <AccountSidebar
            items={navItems}
            variant="cuenta"
            onItemClick={() => setDrawerOpen(false)}
          />
        </Drawer.Root>
      </div>

      <Footer />
      {/* Flecha "volver al inicio" — disponible en /cuenta y subrutas. */}
      <ScrollToTopButton />
    </div>
  );
}
